import apiClient from "./api";

export type CustomerRole = "Admin" | "Member";

export interface Customer {
  id: string;
  customerId: string;
  customerName?: string;
  customerEmail?: string;
  /** Role within the customer's CustomerCompany. */
  role: CustomerRole;
  /** Free-text company identifier originally passed by the widget. Kept for back-compat;
   *  new code should rely on customerCompanyRefId + customerCompanyName. */
  customerCompanyId?: string | null;
  /** FK to the resolved CustomerCompany entity (Guid). Null when the customer
   *  has not yet been associated with a customer company. */
  customerCompanyRefId?: string | null;
  /** Display name of the resolved CustomerCompany. Null when customerCompanyRefId is null. */
  customerCompanyName?: string | null;
  customerSummary?: string;
  companyId: string;
  createdAt: string;
  lastUpdatedAt: string;
  hasPersona: boolean;
  /** Timestamp of the last successful Customer Persona Synthesis run for this customer. */
  personaLastAnalyzedAt?: string | null;
}

export interface CustomerPersona {
  customerId: string;
  toneOfVoice?: string;
  formalityLevel?: string;
  emotionalExpression?: string;
  directnessIndirectness?: string;
  stressLevelFrustration?: string;
  patienceUrgency?: string;
  confidenceUncertainty?: string;
  attentionToDetail?: string;
  primaryGoals?: string;
  engagementType?: string;
  decisionStage?: string;
  trustLevelOpenness?: string;
  cooperationLevel?: string;
  politenessEmpathy?: string;
  learningStyle?: string;
  decisionMakingStyle?: string;
  engagementPattern?: string;
  lastUpdatedAt: string;
}

export interface CustomerDetail extends Customer {
  persona?: CustomerPersona;
}

export interface CustomerToolkitAuthorization {
  serverId: string;
  serverName: string | null;
  authorizationType: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerMemory {
  id: string;
  content: string;
  category: string;
  source: string;
  salience: number;
  status: string;
  scope: string;
  effectiveAt: string;
  expiresAt: string | null;
  createdAt: string;
}

export interface CustomerChatSummary {
  id: string;
  title: string;
  createdAt: string;
  lastUpdatedAt: string;
  messageCount: number;
  toolExecutionCount: number;
}

/** A file the end user attached to a chat message. Metadata only — bytes stay in S3. */
export interface CustomerChatAttachment {
  id: string;
  fileName?: string;
  mimeType: string;
  fileSizeBytes: number;
}

export interface CustomerChatTimelineItem {
  itemType: "message" | "ai_execution" | "tool_execution";
  itemId: string;
  timestamp: string;
  title: string;
  preview?: string;
  messageRole?: string;
  isSuccess?: boolean;
  httpStatusCode?: number;
  responseId?: string;
  toolCallId?: string;
  hasFeedback?: boolean;
  feedbackVote?: string;
  feedbackReasonTag?: string;
  /** Only populated for itemType === "message". */
  attachments?: CustomerChatAttachment[];
}

export interface CustomerChatTimeline {
  sessionId: string;
  title: string;
  createdAt: string;
  lastUpdatedAt: string;
  items: CustomerChatTimelineItem[];
}

export interface UpdateCustomerRequest {
  customerName?: string;
  customerSummary?: string;
  role?: CustomerRole;
  /**
   * Assign the customer to a CustomerCompany. Pass the CC Guid to (re)assign,
   * pass `"00000000-0000-0000-0000-000000000000"` to clear the association,
   * or omit to leave it untouched. The backend uses the empty Guid sentinel
   * because JSON can't carry an explicit null for a Guid?.
   */
  customerCompanyRefId?: string;
}

export interface UpdateCustomerPersonaRequest {
  toneOfVoice?: string;
  formalityLevel?: string;
  emotionalExpression?: string;
  directnessIndirectness?: string;
  stressLevelFrustration?: string;
  patienceUrgency?: string;
  confidenceUncertainty?: string;
  attentionToDetail?: string;
  primaryGoals?: string;
  engagementType?: string;
  decisionStage?: string;
  trustLevelOpenness?: string;
  cooperationLevel?: string;
  politenessEmpathy?: string;
  learningStyle?: string;
  decisionMakingStyle?: string;
  engagementPattern?: string;
}

export interface CustomerWorkflowSchedule {
  id: string;
  workflowId: string;
  frequencyType: "Daily" | "Weekdays" | "Weekly" | "Monthly";
  timeOfDay: string;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  timezone: string;
  isActive: boolean;
  nextRunAt: string | null;
}

export interface CustomerWorkflowLog {
  id: string;
  /** `Cancelled` means the schedule fired while the workflow was inactive, so the run was skipped. */
  status: "Pending" | "Running" | "Completed" | "Failed" | "Cancelled";
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  chatSessionId: string | null;
}

export interface CustomerWorkflow {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  schedules: CustomerWorkflowSchedule[];
  recentLogs: CustomerWorkflowLog[];
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Activity / origin buckets for the Customers list. Selected buckets are OR-ed on the
 * server and AND-ed with the other filters; an empty array applies no bucket filter.
 * Buckets overlap by design — a preview visitor who chatted matches both `preview`
 * and `hasSession`.
 */
export type CustomerSegment =
  /** At least one chat session — customers who actually talked to the agent. */
  | "hasSession"
  /** Widget interaction logged but no session ever created (bootstrap "ghost" rows). */
  | "openedNoChat"
  /** Id minted by the server (`anon-`) or by the widget itself (`user-`). */
  | "anonymous"
  /** Id minted by an admin-app widget preview (`preview…`). */
  | "preview"
  /** Neither a session nor an interaction — e.g. CSV-imported or portal-registered. */
  | "noActivity";

export const CUSTOMER_SEGMENTS: CustomerSegment[] = [
  "hasSession",
  "openedNoChat",
  "anonymous",
  "preview",
  "noActivity",
];

export interface GetCustomersParams {
  search?: string;
  customerCompanyRefId?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  segments?: CustomerSegment[];
  page?: number;
  pageSize?: number;
}

export const customerService = {
  getAllCustomers: async (
    params: GetCustomersParams = {},
  ): Promise<PaginatedResult<Customer>> => {
    const response = await apiClient.get("/customers", {
      params: {
        search: params.search || undefined,
        customerCompanyRefId: params.customerCompanyRefId || undefined,
        dateFrom: params.dateFrom || undefined,
        dateTo: params.dateTo || undefined,
        // Comma-separated to match the endpoint's flat query-param style.
        segments: params.segments?.length
          ? params.segments.join(",")
          : undefined,
        page: params.page ?? 1,
        pageSize: params.pageSize ?? 20,
      },
    });
    return response.data;
  },

  getCustomer: async (id: string): Promise<CustomerDetail> => {
    const response = await apiClient.get(`/customers/${id}`);
    return response.data;
  },

  updateCustomer: async (
    id: string,
    data: UpdateCustomerRequest,
  ): Promise<Customer> => {
    const response = await apiClient.put(`/customers/${id}`, data);
    return response.data;
  },

  updateCustomerPersona: async (
    id: string,
    data: UpdateCustomerPersonaRequest,
  ): Promise<CustomerPersona> => {
    const response = await apiClient.put(`/customers/${id}/persona`, data);
    return response.data;
  },

  getCustomerAuthorizations: async (
    id: string,
  ): Promise<CustomerToolkitAuthorization[]> => {
    const response = await apiClient.get(`/customers/${id}/authorization`);
    return response.data;
  },

  clearCustomerAuthorization: async (id: string): Promise<void> => {
    await apiClient.delete(`/customers/${id}/authorization`);
  },

  getCustomerChats: async (id: string): Promise<CustomerChatSummary[]> => {
    const response = await apiClient.get(`/customers/${id}/chats`);
    return response.data;
  },

  getCustomerChatTimeline: async (
    customerId: string,
    chatId: string,
  ): Promise<CustomerChatTimeline> => {
    const response = await apiClient.get(
      `/customers/${customerId}/chats/${chatId}/timeline`,
    );
    return response.data;
  },

  getCustomerWorkflows: async (id: string): Promise<CustomerWorkflow[]> => {
    const response = await apiClient.get(`/customers/${id}/workflows`);
    return response.data;
  },

  getCustomerMemories: async (
    id: string,
    includeInactive = false,
  ): Promise<CustomerMemory[]> => {
    const response = await apiClient.get(`/customers/${id}/memories`, {
      params: { includeInactive },
    });
    return response.data;
  },

  deleteCustomerMemory: async (id: string, memoryId: string): Promise<void> => {
    await apiClient.delete(`/customers/${id}/memories/${memoryId}`);
  },
};
